import { useState, useContext, useEffect } from 'react';

import { UserContentValuesContext } from 'contexts';

/**
 * Provide a wrapper for getState around user content items
 * such as highlights, notes, copied texts, and flashcards.
 *
 * It ensures that if the Apollo cache or IndexedDB content changes,
 * the user content item will stay in the state or be replaced
 * with an updated object with the same ID or UUID.
 *
 * @method useUserContentItem
 * @param {UserContentItem|null} initialValue
 */
export default function useUserContentItem(initialValue = null) {
  const userContent = useContext(UserContentValuesContext);

  const [item, setItem] = useState(null);
  const [referenceItem, setReferenceItem] = useState(initialValue);

  useEffect(() => {
    (async () => {
      if (!referenceItem) {
        setItem(null);

        return;
      }

      const idToFind = await referenceItem.id;

      for (let userContentItem of userContent[
        referenceItem.constructor.typeName
      ]) {
        if (userContentItem.id && idToFind && userContentItem.id === idToFind) {
          // id matches item in user content object
          return setItem(userContentItem);
        }

        if (userContentItem.uuid === referenceItem.uuid) {
          // uuid matches item in user content object
          return setItem(userContentItem);
        }
      }

      // If the reference item does not match anything in the user content object,
      // keep this orphaned item as the item state until it is changed or becomes
      // available as part of the main UserContent bundle
      setItem(referenceItem);
    })();
  }, [referenceItem, userContent]);

  // Set an initial value using the callback
  useEffect(() => {
    setItem(initialValue);
  }, [initialValue, setItem]);

  return [item, setReferenceItem];
}
